import styled from 'styled-components';
import { layout } from 'styled-system';
import PropTypes from 'prop-types';

import ReferenceQuestionRow from './ReferenceQuestionRow';
import QuestionBox from '../common/QuestionBox';

import grayArrow from '../../assets/icon_right_caret_grey.svg';
import { ReactComponent as RightArrow } from 'assets/icon_right_caret_grey.svg';

import { referenceQuestionOptions, referencePermissionsSuper } from '../../utils/enums';
import auth from 'utils/auth';

import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { useHistory } from 'react-router-dom';

const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

const getItemStyle = (isDragging, draggableStyle) => ({
  // some basic styles to make the items look a bit nicer
  userSelect: 'none',
  padding: '8px 0',

  // change background colour if dragging
  // background: isDragging ? "lightgreen" : "grey",
  // boxShadow: isDragging ? "rgba(0, 0, 0, 0.2) 0px 3px 3px 0px" : 0,

  // styles we need to apply on draggables
  ...draggableStyle,
});

const getListStyle = (isDraggingOver) => ({
  // background: isDraggingOver ? "lightblue" : "lightgrey",
  // padding: grid,
});

export default function ReferenceEdit({
  reference,
  setQuestionOrder,
  openNewQuestionModal,
  openCopyReferenceModal,
  saveReference,
  deleteReference,
  updateField,
  saveQuestion,
  deleteQuestion,
  questionBank,
  errorMessage,
}) {
  const history = useHistory();
  const draggableQuestions = () => (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="droppable">
        {(provided, snapshot) => (
          <div
            {...provided.droppableProps}
            ref={provided.innerRef}
            style={getListStyle(snapshot.isDraggingOver)}
          >
            {reference.questions.map((item, index) => (
              <Draggable
                key={item.draggable_id}
                draggableId={String(item.draggable_id)}
                index={index}
              >
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
                  >
                    <ReferenceQuestionRow
                      id={index}
                      key={item.draggable_id}
                      question={item}
                      updateField={updateField}
                      saveQuestion={saveQuestion}
                      deleteQuestion={deleteQuestion}
                      questionBank={questionBank}
                    />
                  </div>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );

  const onDragEnd = (result) => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }

    const reorderedItems = reorder(
      reference.questions,
      result.source.index,
      result.destination.index
    );

    setQuestionOrder(reorderedItems);
  };

  const urlPrefix = auth.isSchoolAdmin() ? 'school' : 'district';

  return (
    <>
      <div className="edit-scorecard-header flex">
        <div
          className="scorecard-back-button"
          onClick={() => history.push(`/${urlPrefix}/settings`)}
        >
          <img src={grayArrow} alt="" />
          <span className="Scorecard">Return to settings page</span>
        </div>
        <h3 className="mr1 flex-3">
          {reference.id === undefined ? 'Create New Reference Form' : 'Edit Reference'}
        </h3>
      </div>
      <div className="jobedit-container-padding">
        <HeaderText>Reference Form Name</HeaderText>
        <ReferenceNameInput
          name="title"
          type="text"
          placeholder="eg. Teacher Reference Check Form"
          value={reference.title}
          onChange={updateField}
        />
        <HeaderText>Reference Form Items</HeaderText>
        {reference.questions.length === 0 ? (
          <GreyText>
            Select an item below to start building or
            <WorkFromExisting onClick={openCopyReferenceModal}>
              work from an existing reference form
            </WorkFromExisting>
            <RightCaret />
          </GreyText>
        ) : (
          draggableQuestions()
        )}
        {auth.isSuperAdminUser() && (
          <>
            <HeaderText>Permissions</HeaderText>
            <GreyText>
              This reference form will be visible to:
              <VisibilityDropdown
                name="view_permissions"
                value={reference.view_permissions}
                onChange={updateField}
              >
                {referencePermissionsSuper.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </VisibilityDropdown>
            </GreyText>
          </>
        )}
        <HeaderText>Add Items</HeaderText>
        <div className="question-type-container mb4">
          {referenceQuestionOptions().map((item) => (
            <QuestionBox
              key={item.key}
              handleClick={openNewQuestionModal}
              label={item.label}
              type={item.value}
            />
          ))}
        </div>
        <ActionButtonsContainer>
          {reference.id && (
            <DeleteButton
              onClick={deleteReference}
              width={[1, 1, '150px']}
              minWidth={[1, 1, '150px']}
            >
              Delete form
            </DeleteButton>
          )}
          <SubmitButtonContainer>
            {errorMessage && <ErrorTextStyled>{errorMessage}</ErrorTextStyled>}
            <SaveButton onClick={saveReference} width={[1, 1, '150px']} minWidth={[1, 1, '150px']}>
              Save form
            </SaveButton>
          </SubmitButtonContainer>
        </ActionButtonsContainer>
      </div>
    </>
  );
}

ReferenceEdit.propTypes = {
  reference: PropTypes.object.isRequired,
  setQuestionOrder: PropTypes.func.isRequired,
  openNewQuestionModal: PropTypes.func.isRequired,
  saveReference: PropTypes.func.isRequired,
  updateField: PropTypes.func.isRequired,
};

const ReferenceNameInput = styled.input`
  background-color: #fff;
  padding: 20px;
  border: 0;
  box-shadow: 0 3px 3px 0 rgba(0, 0, 0, 0.08);
  border-radius: 3px;
  height: 50px;
  width: 100%;

  &::placeholder {
    color: #999999;
    opacity: 0.6;
  }
`;

const HeaderText = styled.p`
  font-weight: 600;
  font-size: 16px;
  line-height: 22px;
  color: #444444;

  margin-bottom: 8px;
  margin-top: 28px;
`;

const GreyText = styled.p`
  font-weight: normal;
  font-size: 14px;
  line-height: 19px;

  color: #999999;
`;

const WorkFromExisting = styled.u`
  cursor: pointer;
  margin: 0 4px;

  &:hover {
    color: #00b88d;
  }
`;

const RightCaret = styled(RightArrow)`
  height: 10px;
`;

const ActionButtonsContainer = styled.div`
  display: grid;
  grid-row-gap: 12px;

  margin-top: 80px;

  @media (min-width: 768px) {
    grid-template-columns: 1fr minmax(258px, 1fr);
    grid-column-gap: 12px;
  }
`;

const ActionButton = styled.button`
  ${layout}

  height: 50px !important;
  box-shadow: none !important;
  font-size: 16px;
  border-radius: 3px;
`;

const DeleteButton = styled(ActionButton)`
  color: #ef5675 !important;
  background-color: #ffffff !important;
  border: 1px solid #ef5675 !important;
`;

const SaveButton = styled(ActionButton)`
  color: white !important;
  background-color: #00b88d !important;
  border: none !important;
`;

const SubmitButtonContainer = styled.div`
  @media screen and (min-width: 768px) {
    display: flex;
    align-items: center;
    justify-self: end;
    grid-column-start: 2;
  }
`;

const VisibilityDropdown = styled.select`
  background-color: white;
  border: none;
  padding: 5px 3px;
  margin-left: 6px;
  box-shadow: 0px 2px 2px rgba(0, 0, 0, 0.0954484);
  border-radius: 3px;
`;

const ErrorTextStyled = styled.p`
  color: #ff7700;
  min-height: 1.5rem;
  margin-right: 1rem;
`;
